/**
 * Currencies whose symbol conventionally follows the amount ("700.00 zł")
 * even in locales where Intl would prefix it ("zł 700.00").
 */
const SUFFIX_SYMBOL_CURRENCIES = new Set(['PLN', 'CZK', 'HUF', 'RON', 'BGN', 'SEK', 'DKK', 'NOK']);

/**
 * Localized currency formatting with the narrow symbol (£, €, zł - never the
 * ISO code). Placement follows the locale ("€700.00" in en, "700,00 €" in pl)
 * except for suffix-conventional currencies, which always trail the amount.
 */
export function formatCurrency(amount: number, currency: string, locale: string): string {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  });

  if (!SUFFIX_SYMBOL_CURRENCIES.has(currency.toUpperCase())) {
    return formatter.format(amount);
  }

  const parts = formatter.formatToParts(amount);
  const symbol = parts.find((part) => part.type === 'currency')?.value ?? currency;
  const number = parts
    .filter((part) => part.type !== 'currency')
    .map((part) => part.value)
    .join('')
    .trim();

  return `${number} ${symbol}`;
}

export function formatNumber(value: number, locale: string, fractionDigits = 2): string {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: 0,
  }).format(value);
}
